import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import {
  capture,
  getActiveFeatureFlags,
  getDistinctId,
  getLastEvent,
  isPostHogConfigured,
  isPostHogReady,
} from "@/lib/posthog";

export const Route = createFileRoute("/admin/posthog-diagnostics")({
  ssr: false,
  head: () => ({ meta: [{ title: "PostHog Diagnostics · VerseSport" }] }),
  component: PostHogDiagnosticsPage,
});

function PostHogDiagnosticsPage() {
  const { user, authReady } = useAuth();
  const { isSuperAdmin, isLoading } = useSuperAdmin();
  const [diag, setDiag] = useState<{
    distinctId: string | null;
    flags: string[];
    last: ReturnType<typeof getLastEvent>;
  } | null>(null);

  const configured = isPostHogConfigured();
  const ready = isPostHogReady();

  useEffect(() => {
    if (!ready) return;
    const read = () => {
      try {
        setDiag({
          distinctId: getDistinctId(),
          flags: getActiveFeatureFlags(),
          last: getLastEvent(),
        });
      } catch {
        // Si PostHog falla internamente, mantenemos el estado previo
      }
    };
    read();
    const i = setInterval(read, 2000);
    return () => clearInterval(i);
  }, [ready]);

  if (!authReady || isLoading) {
    return <main className="p-6 text-sm text-muted-foreground">Cargando…</main>;
  }
  if (!isSuperAdmin) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-xl font-bold">Acceso restringido</h1>
          <p className="mt-2 text-sm text-muted-foreground">Requiere rol superadmin.</p>
          <Link to="/" className="mt-4 inline-block text-primary underline">Volver</Link>
        </div>
      </main>
    );
  }

  if (!configured) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <h1 className="text-2xl font-black">PostHog Diagnostics</h1>
        <p className="mt-4 text-sm text-destructive">
          PostHog no está configurado. Revisa src/lib/posthog/config.ts.
        </p>
      </main>
    );
  }

  if (!ready) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <h1 className="text-2xl font-black">PostHog Diagnostics</h1>
        <p className="mt-4 text-sm text-muted-foreground">Inicializando PostHog…</p>
      </main>
    );
  }

  const distinctId = diag?.distinctId ?? null;
  const flags = diag?.flags ?? [];
  const last = diag?.last ?? null;

  return (
    <main className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-black">PostHog Diagnostics</h1>
        <p className="text-sm text-muted-foreground">Estado de la integración Product Analytics + Session Replay + Feature Flags.</p>
      </header>

      <section className="space-y-2 rounded-xl border border-border/60 p-4">
        <Row label="Configurado (clave phc_…)" value={configured ? "sí" : "NO — pegar clave en src/lib/posthog/config.ts"} ok={configured} />
        <Row label="Cliente inicializado" value={ready ? "sí" : "no"} ok={ready} />
        <Row label="Host" value="https://us.i.posthog.com" />
        <Row label="Distinct ID" value={distinctId ?? "—"} />
        <Row label="Usuario identificado" value={user?.id ? `${user.id} (${user.email ?? "no email"})` : "anónimo"} />
        <Row label="Feature flags activos" value={flags.length ? flags.join(", ") : "ninguno"} />
      </section>

      <section className="space-y-2 rounded-xl border border-border/60 p-4">
        <h2 className="text-sm font-bold">Último evento</h2>
        {last ? (
          <pre className="overflow-auto rounded-md bg-accent/30 p-3 text-[11px]">
{JSON.stringify({ name: last.name, at: new Date(last.at).toISOString(), props: last.props }, null, 2)}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground">Sin eventos enviados todavía en esta sesión.</p>
        )}
      </section>

      <section className="flex flex-wrap gap-3">
        <button
          onClick={() => capture("diagnostics_test_event", { source: "admin_panel", ts: Date.now() })}
          disabled={!ready}
          className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
        >
          Send Test Event
        </button>
        <a
          href="https://us.posthog.com"
          target="_blank"
          rel="noreferrer"
          className="rounded-md border border-input bg-background px-4 py-2 text-sm font-bold"
        >
          Abrir dashboard PostHog ↗
        </a>
      </section>
    </main>
  );
}

function Row({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/30 py-1.5 last:border-b-0">
      <span className="text-xs font-bold text-muted-foreground">{label}</span>
      <span className={`text-xs font-mono ${ok === false ? "text-destructive" : ok === true ? "text-primary" : ""}`}>{value}</span>
    </div>
  );
}